import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useParams } from "react-router";
import { useState } from "react";
import ExportIntakesToCsvStatusTable from "../components/Intakes/ExportIntakesToCsvStatusTable";
import IntakesTable from "../components/Intakes/IntakesTable";
import DeleteIntakesTable from "../components/Intakes/DeleteIntakesTable";
import { Intake } from "../models/Intake";
import { DeleteIntakeRequest } from "../models/Jobs/JobRequest";
import CreateExportIntakesToCsvTable from "../components/Intakes/CreateExportIntakesToCsvTable";
import {UserRole} from "../models/User";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";

export enum ActiveIntakeJobsExplorer {
  DeleteIntakes = 0,
  GenerateCSV = 1,
}

interface IntakesPageProps {
  userRole: UserRole
  isAssignedUser: boolean;
}

const IntakesPage: React.FC<IntakesPageProps> = ({
  userRole,
  isAssignedUser
}: IntakesPageProps) => {
  const { organizationId } = useParams();
  const [newIntakesForDeletion, setNewIntakesForDeletion] = useState<DeleteIntakeRequest[]>([]);
  const [newIntakesForExport, setNewIntakesForExport] = useState<Intake[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveIntakeJobsExplorer>(ActiveIntakeJobsExplorer.GenerateCSV);
  const queuedCount = newIntakesForDeletion.length + newIntakesForExport.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Intakes</h2>
        {userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage
            {queuedCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {queuedCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <IntakesTable
            organizationId={organizationId ?? ""}
            activeJobsExplorer={selectedTab}
            newIntakesForDeletion={newIntakesForDeletion}
            setNewIntakesForDeletion={setNewIntakesForDeletion}
            newIntakesForExport={newIntakesForExport}
            setNewIntakesForExport={setNewIntakesForExport}
            userRole={userRole}
            isAssignedUser={isAssignedUser}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage intakes</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveIntakeJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveIntakeJobsExplorer.DeleteIntakes)} className="flex-1">
                    Delete intakes
                  </ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveIntakeJobsExplorer.GenerateCSV)} className="flex-1">
                  Export CSV
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 min-h-0 flex flex-col">
                {selectedTab === ActiveIntakeJobsExplorer.DeleteIntakes && (userRole === UserRole.Admin || isAssignedUser) && (
                  <DeleteIntakesTable
                    organizationId={organizationId ?? ""}
                    newIntakesForDeletion={newIntakesForDeletion}
                    setNewIntakesForDeletion={setNewIntakesForDeletion}
                  />
                )}
                {selectedTab === ActiveIntakeJobsExplorer.GenerateCSV && (
                  <div className="flex-1 overflow-auto min-h-0">
                    <div className="flex flex-col h-full gap-4">
                      {newIntakesForExport.length > 0 && (
                        <>
                          <div className="flex items-center gap-3 py-2 pt-4">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                            <Separator className="flex-1" />
                          </div>
                          <CreateExportIntakesToCsvTable
                            organizationId={organizationId ?? ""}
                            newIntakesForExport={newIntakesForExport}
                            setNewIntakesForExport={setNewIntakesForExport}
                            userRole={userRole}
                            isAssignedUser={isAssignedUser}
                          />
                          <div className="flex items-center gap-3 py-2">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>Existing exports</b></span>
                            <Separator className="flex-1" />
                          </div>
                        </>
                      )}
                      <ExportIntakesToCsvStatusTable
                        organizationId={organizationId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntakesPage;
